import { useCallback, useEffect, useState } from 'react';
import screens from './Screens.module.css';
import styles from './Forecast.module.css';
import {
  Badge,
  Button,
  ChoiceCard,
  ChoiceGrid,
  ChoiceList,
  ChoiceRow,
  Spinner,
  SplitBar,
  Stepper,
} from '../../components/ui';
import {
  cooldownFrom,
  generateForecast,
  getDates,
  getLeagues,
  getMatches,
  getSports,
} from '../../api/platform.js';
import { parseForecast } from '../../api/forecastText.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { formatDate, useI18n } from '../../i18n/index.jsx';

/**
 * The forecast wizard: sport → date → league → match → result.
 *
 * All five calls hit the real API. Two things about the backend shape drive
 * this screen:
 *   - the date and league lists carry no match counts, so rows show no detail
 *   - POST /api/forecast returns one text blob, parsed by parseForecast()
 */
export function Forecast() {
  const { t, lang } = useI18n();
  const { isPaid } = useAuth();

  const [step, setStep] = useState(1);
  const [choice, setChoice] = useState({ sport: null, date: null, league: null, match: null });
  // `forStep` records which step the loaded list belongs to, so a step never
  // renders the previous step's options while its own fetch is in flight.
  const [options, setOptions] = useState({ list: null, error: null, forStep: 0 });
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [cooldown, setCooldown] = useState(null);
  const [forecastError, setForecastError] = useState('');

  useEffect(() => {
    document.title = `${t('wizard_title')} — Don Quack`;
  }, [t]);

  const loadStep = useCallback(
    async (n, picked) => {
      setOptions({ list: null, error: null, forStep: n });
      try {
        let list = null;
        if (n === 1) list = await getSports();
        else if (n === 2) list = await getDates(picked.sport.key);
        else if (n === 3) list = await getLeagues(picked.sport.key, picked.date.key);
        else if (n === 4)
          list = await getMatches(picked.sport.key, picked.date.key, picked.league.key);
        setOptions({ list, error: null, forStep: n });
      } catch (err) {
        setOptions({ list: null, error: err.message || t('error_generic'), forStep: n });
      }
    },
    [t]
  );

  useEffect(() => {
    if (step <= 4) loadStep(step, choice);
    // `choice` is read, not watched: a selection advances the step, and the
    // step change is what should trigger the reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, loadStep]);

  function pick(key, value) {
    const next = { ...choice, [key]: value };
    if (key === 'sport') Object.assign(next, { date: null, league: null, match: null });
    if (key === 'date') Object.assign(next, { league: null, match: null });
    if (key === 'league') Object.assign(next, { match: null });

    setOptions((o) => ({ ...o, list: null }));
    setChoice(next);

    if (key === 'match') runForecast(next);
    else setStep((s) => s + 1);
  }

  async function runForecast(picked) {
    setStep(5);
    setGenerating(true);
    setResult(null);
    setCooldown(null);
    setForecastError('');

    try {
      const text = await generateForecast(picked);
      setResult({ ...parseForecast(text, picked.match), match: picked.match, league: picked.league });
    } catch (err) {
      // A free account that has used its allowance isn't an error state — it's
      // a "come back later" state, with its own message and an upgrade path.
      const waiting = cooldownFrom(err);
      if (waiting) setCooldown(waiting);
      else setForecastError(err.message || t('error_generic'));
    } finally {
      setGenerating(false);
    }
  }

  function restart() {
    setChoice({ sport: null, date: null, league: null, match: null });
    setResult(null);
    setCooldown(null);
    setForecastError('');
    setStep(1);
  }

  const steps = [
    t('step_sport'),
    t('step_date'),
    t('step_league'),
    t('step_match'),
    t('step_forecast'),
  ];

  const listReady = (n) => options.list && options.forStep === n;
  const listEmpty = (n) => listReady(n) && options.list.length === 0;

  return (
    <section>
      <h2 className={screens.title}>{t('wizard_title')}</h2>
      <p className={screens.sub}>{t('wizard_subtitle')}</p>

      <Stepper
        steps={steps}
        current={step}
        onStepClick={(n) => {
          setResult(null);
          setCooldown(null);
          setForecastError('');
          setStep(n);
        }}
        className={styles.stepper}
      />

      {options.error && (
        <div className={screens.errorBox}>
          <span>{options.error}</span>
          <Button size="sm" variant="secondary" onClick={() => loadStep(step, choice)}>
            {t('retry')}
          </Button>
        </div>
      )}

      {/* ---- step 1: sport ---- */}
      {step === 1 &&
        (listReady(1) ? (
          <ChoiceGrid>
            {options.list.map((s) => (
              <ChoiceCard
                key={s.key}
                emoji={s.emoji}
                label={s.name}
                selected={choice.sport?.key === s.key}
                onClick={() => pick('sport', s)}
              />
            ))}
          </ChoiceGrid>
        ) : (
          <Loading />
        ))}

      {/* ---- step 2: date ---- */}
      {step === 2 &&
        (listEmpty(2) ? (
          <Empty message={t('no_dates_found')} />
        ) : listReady(2) ? (
          <ChoiceList>
            {options.list.map((d) => (
              <ChoiceRow
                key={d.key}
                label={dateLabel(d, lang, t)}
                selected={choice.date?.key === d.key}
                onClick={() => pick('date', d)}
              />
            ))}
          </ChoiceList>
        ) : (
          <Loading />
        ))}

      {/* ---- step 3: league ---- */}
      {step === 3 &&
        (listEmpty(3) ? (
          <Empty message={t('no_matches_found')} />
        ) : listReady(3) ? (
          <ChoiceList>
            {options.list.map((l) => (
              <ChoiceRow
                key={l.key}
                label={l.name}
                selected={choice.league?.key === l.key}
                onClick={() => pick('league', l)}
              />
            ))}
          </ChoiceList>
        ) : (
          <Loading />
        ))}

      {/* ---- step 4: match ---- */}
      {step === 4 &&
        (listEmpty(4) ? (
          <Empty message={t('no_matches_found')} />
        ) : listReady(4) ? (
          <ChoiceList>
            {options.list.map((m) => (
              <ChoiceRow
                key={m.id}
                label={`${m.home} vs ${m.away}`}
                detail={m.time}
                selected={choice.match?.id === m.id}
                onClick={() => pick('match', m)}
              />
            ))}
          </ChoiceList>
        ) : (
          <Loading />
        ))}

      {/* ---- step 5: the forecast ---- */}
      {step === 5 && (
        <>
          {generating && (
            <div className={styles.generating}>
              <Spinner />
              <h3>{t('forecast_generating')}</h3>
              <p className="muted">{t('forecast_generating_note')}</p>
            </div>
          )}

          {cooldown && (
            <div className={styles.cooldown}>
              <span className={styles.cooldownIcon} aria-hidden="true">
                ⏳
              </span>
              <h3>{t('cooldown_title')}</h3>
              <p className="muted">
                {t('cooldown_body', { time_remaining: cooldown.timeRemaining })}
              </p>
              <div className={styles.cooldownActions}>
                {!isPaid && <Button to="/app/pricing">{t('cooldown_upgrade')}</Button>}
                <Button variant="secondary" onClick={restart}>
                  {t('forecast_new')}
                </Button>
              </div>
            </div>
          )}

          {forecastError && (
            <div className={screens.errorBox}>
              <span>{forecastError}</span>
              <Button size="sm" variant="secondary" onClick={() => runForecast(choice)}>
                {t('retry')}
              </Button>
            </div>
          )}

          {result && <ForecastResult result={result} onRestart={restart} />}
        </>
      )}

      {step > 1 && step < 5 && (
        <div className={styles.nav}>
          <Button variant="secondary" onClick={() => setStep((s) => s - 1)}>
            {t('btn_back')}
          </Button>
        </div>
      )}
    </section>
  );
}

function Loading() {
  return (
    <div className={screens.loadingBlock}>
      <Spinner />
    </div>
  );
}

function Empty({ message }) {
  return (
    <div className={screens.empty}>
      <p>{message}</p>
    </div>
  );
}

/** The API sends bare 'YYYY-MM-DD', so Today/Tomorrow is decided here. */
function dateLabel(d, lang, t) {
  if (!d.date) return d.key;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((d.date - today) / 86400000);

  const relative = days === 0 ? t('today') : days === 1 ? t('tomorrow') : null;
  const absolute = formatDate(d.date, lang, { day: 'numeric', month: 'long' });

  return relative
    ? `${relative} · ${absolute}`
    : `${formatDate(d.date, lang, { weekday: 'short' })} · ${absolute}`;
}

function ForecastResult({ result, onRestart }) {
  const { t } = useI18n();
  const { match, league, probabilities, analysis, insights, raw } = result;

  return (
    <div className={styles.result}>
      <div className={styles.league}>
        {league?.name} · {match.time}
      </div>

      <div className={styles.teams}>
        <span className={styles.team}>{match.home}</span>
        <span className={styles.vs}>VS</span>
        <span className={styles.team}>{match.away}</span>
      </div>

      {probabilities ? (
        <>
          <SplitBar
            homeProbability={probabilities.homeProbability}
            winner={probabilities.winner}
            size="lg"
            homeLabel={match.home}
            awayLabel={match.away}
          />

          <div className={styles.verdict}>
            <span className="muted">
              {t('forecast_predicted_winner')} ·{' '}
              <b className={styles.winner}>{probabilities.predictedWinner}</b>
            </span>
            <Badge tone={probabilities.confidence} dot>
              {t(`confidence_${probabilities.confidence}`)}
            </Badge>
          </div>

          {analysis && (
            <div className={styles.analysis}>
              <b>{t('forecast_analysis')}.</b> {analysis}
            </div>
          )}

          {insights && (
            <div className={styles.insightBlock}>
              <h4>{t('forecast_insights_title')}</h4>
              <p>{insights}</p>
            </div>
          )}
        </>
      ) : (
        /* The probabilities couldn't be read out of the text. Rather than hide a
           forecast the user is entitled to, show exactly what the API returned. */
        <pre className={styles.rawForecast}>{raw}</pre>
      )}

      <div className={styles.nav}>
        <Button variant="secondary" onClick={onRestart}>
          {t('forecast_new')}
        </Button>
        <Button to="/app/history">{t('nav_history')}</Button>
      </div>
    </div>
  );
}
